// ** React Imports
import { Fragment, lazy } from "react"
import { Navigate } from "react-router-dom"
// ** Layouts
import BlankLayout from "@layouts/BlankLayout"
import VerticalLayout from "@src/layouts/VerticalLayout"
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper"

// ** Route Components
import PublicRoute from "@components/routes/PublicRoute"

// ** Utils
import { isObjEmpty } from "@utils"

const getLayout = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />
}

// ** Document title
const TemplateTitle = "%s - BlockPay"

// ** Default Route
const DefaultRoute = "/home"

const Home = lazy(() => import("../../views/Home"))
const Transactions = lazy(() => import("../../views/transactions/index"))
const Transfer = lazy(() => import("../../views/transfer/index"))
const Investment = lazy(() => import("../../views/investment/index"))
const InvestmentWithdraw = lazy(() => import("../../views/investment/withdraw"))
const BlockInvest = lazy(() => import("../../views/block-invest/index"))
const Faq = lazy(() => import("../../views/faq/index"))
const Legal = lazy(() => import("../../views/legal/index"))
const Profile = lazy(() => import("../../views/user/account-settings"))
const ForgotPassword = lazy(() => import("../../views/auth/ForgotPassword"))
const Error = lazy(() => import("../../views/Error"))
const SignatureComplete = lazy(() => import("../../views/credit/SignatureComplete"))

// ** Quién entra y quién no lo decide VerticalLayout, que es el padre de todas estas rutas.
//
// Aquí había un `const user = localStorage.getItem("userInfo")` leído UNA vez, al importar el
// módulo —es decir, estando en el login, cuando todavía no hay sesión—, y cada `element` se
// construía con ese valor congelado. Al entrar sin doble autenticación, que es el único camino
// que no recarga la página, las rutas privadas seguían creyendo que no había nadie y mandaban
// a /login; /login leía la sesión recién creada y mandaba de vuelta a /home. Ida y vuelta sin
// fin: la barra de arriba pintada y el contenido en blanco hasta pulsar F5, que era lo único
// que volvía a evaluar el módulo.
//
// Comprobarlo en el layout lo resuelve de raíz: se evalúa al renderizar, siempre, y en un solo
// sitio en vez de repetido en cada ruta —donde además faltaba en algunas—.

// ** Merge Routes
const Routes = [
  {
    path: "/home",
    element: <Home />
  },
  {
    path: "/transactions",
    element: <Transactions /> 
  },
  {
    path: "/block-invest",
    element: <BlockInvest />
  },
  {
    path: "/transfer",
    element: <Transfer />
  },
  // ** Legal: el diseño une Disclaimer y Privacidad en una pantalla con pestañas.
  {
    path: "/legal/:doc",
    element: <Legal />
  },
  {
    path: "/legal",
    element: <Navigate replace to="/legal/disclaimer" />
  },
  // Rutas anteriores a la unificación. Se conservan como redirección porque pueden
  // estar en enlaces de correo o guardadas por los usuarios.
  {
    path: "/privacy",
    element: <Navigate replace to="/legal/privacy" />
  },
  {
    path: "/disclaimer",
    element: <Navigate replace to="/legal/disclaimer" />
  },
  // ** Inversiones: el diseño tiene una sola pantalla, no summary + detail.
  {
    path: "/investment",
    element: <Investment />
  },
  {
    path: "/investment/summary",
    element: <Navigate replace to="/investment" />
  },
  {
    path: "/investment/detail",
    element: <Navigate replace to="/investment" />
  },
  {
    path: "/investment/withdraw",
    element: <InvestmentWithdraw />
  },
  {
    path: "/faq",
    element: <Faq />
  },
  {
    path: "/profile",
    element: <Profile />
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    meta: {
      layout: "blank"
    }
  },
  {
    path: "/error",
    element: <Error />,
    meta: {
      layout: "blank"
    }
  },
  // ** A donde vuelve el iframe del acuerdo del cupo al terminar de firmar.
  //
  // Es el `redirect_url` configurado en eSignatures. Con layout `blank` a propósito: se carga
  // dentro de un iframe de 480 px y con el layout normal arrancaría la aplicación completa
  // —menú, sesión y sus consultas— para una página que se descarta al instante.
  //
  // Sin comprobar sesión, y no por descuido: quien la abre es el proveedor de firma, no el
  // navegador de la persona, y mandarla a /login desde dentro del iframe rompería el flujo
  // justo al terminar de firmar. No enseña nada que haya que proteger.
  {
    path: "/signature-complete",
    element: <SignatureComplete />,
    meta: {
      layout: "blank"
    }
  }
]

const getRouteMeta = (route) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta }
    } else {
      return {}
    }
  }
}

// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout, defaultLayout) => {
  const LayoutRoutes = []

  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false
      // ** Checks if Route layout or Default layout matches current layout
      if (
        (route.meta && route.meta.layout && route.meta.layout === layout) ||
        ((route.meta === undefined || route.meta.layout === undefined) &&
          defaultLayout === layout)
      ) {
        const RouteTag = PublicRoute

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === "blank" ? (isBlank = true) : (isBlank = false)
        }
        if (route.element) {
          const Wrapper =
            // eslint-disable-next-line multiline-ternary
            isObjEmpty(route.element.props) && isBlank === false
              ? // eslint-disable-next-line multiline-ternary
              LayoutWrapper
              : Fragment

          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          )
        }

        // Push route to LayoutRoutes
        LayoutRoutes.push(route)
      }
      return LayoutRoutes
    })
  }
  return LayoutRoutes
}

const getRoutes = (layout) => {
  const defaultLayout = layout || "vertical"
  const layouts = ["vertical", "blank"]

  const AllRoutes = []

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout)

    AllRoutes.push({
      path: "/",
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes
    })
  })
  return AllRoutes
}

export { DefaultRoute, TemplateTitle, Routes, getRoutes }
